/*
  This content is mostly be copied over from MakePayment.tsx and still needs a cleanup/refactor itself
*/
import { compact } from 'lodash';
import { useCallback, useState } from 'react';

import {
  CompanyFragment,
  LoanBillSettingsType,
  PaymentMethod,
  PortalPaymentStopSettingType,
} from '@willow/graphql-iso/src/portal';
import { getChargesDue } from '@willow/shared-iso';

import { PortalSelectedLoan } from '../../../App';
import { getTotalAmountDue } from '../../../pages/make-payment/utils';
import { PaymentFormAlerts } from '../../payment-form/alerts/PaymentFormAlerts';
import { PaymentForm } from '../../payment-form/PaymentForm';
import { PaymentDetails } from './payment-details/PaymentDetails';
import { PaymentHeader } from './payment-header/PaymentHeader';

import './payment-accepted.scss';

export interface Props {
  company: CompanyFragment;
  loan: PortalSelectedLoan;
  refetch: (poll?: boolean) => void;
}

export const PaymentsAccepted = ({ company, loan, refetch }: Props) => {
  const {
    id: loanId,
    currentSnapshot: {
      loanStatus,
      paymentDueStatus,
      paymentDueSubStatus,
      paymentsDue,
      payments,
      charges,
      autopaySettings,
      activeWorkoutPlan,
      offeredWorkoutPlans,
      totalAmountRemainingFromBorrower,
      stopSettings,
      shortageAmount,
      spreadMonths,
      escrowShortageAnalysisId,
      openPayoffStatus,
      delinquentInterestSettings,
      currentBillSettingsType,
      totalStatementAmountDue,
      snapshot: {
        balances: { escrowBalance },
      },
    },
    configuration: { hasInterestReserve },
  } = loan;

  const exactPaymentOnly =
    stopSettings.portalPayments === PortalPaymentStopSettingType.Exact ||
    Boolean(loan.company.adminSettings.exactPaymentsOnly);

  const chargesDue = getChargesDue(charges);
  const totalAmountDue = getTotalAmountDue(loan); // This amount accounts for active workout plans
  const totalAmountDueToReinstateLoan = getTotalAmountDue(loan, true); // Actual amount due without considering workout plans
  const totalUpcomingAmountDue = loan.currentSnapshot.totalUpcomingAmountRemainingFromBorrower;
  const { repayEnabled, manualNachaFileGeneration } = company.adminSettings;

  // Pre-open payment form if there is an amount due
  const [displayPaymentForm, setDisplayPaymentForm] = useState(totalAmountDue > 0);

  // Pending can be actionable and it's good to see last processed payment
  const processingAndRecentPayments = compact([
    ...payments.filter((payment) => payment.method !== PaymentMethod.Reserve && payment.status === 'PENDING'),
    payments.findLast((payment) => payment.status === 'PROCESSED'),
  ]).reverse();

  const handlePaymentSubmission = useCallback(() => {
    setDisplayPaymentForm(false);
  }, []);

  const isForbearanceOrFlexModificationWorkoutPlan =
    (activeWorkoutPlan &&
      ['ForbearanceWorkoutPlan', 'FlexModificationWorkoutPlan'].includes(activeWorkoutPlan.__typename ?? '')) ??
    false;

  return (
    <>
      <PaymentHeader loan={loan} />
      <PaymentFormAlerts
        company={company}
        totalAmountDue={totalAmountDue}
        totalAmountDueToReinstateLoan={totalAmountDueToReinstateLoan}
        totalAmountRemainingFromBorrower={totalAmountRemainingFromBorrower}
        paymentsDue={paymentsDue}
        processingAndRecentPayments={processingAndRecentPayments}
        subStatus={paymentDueSubStatus}
        autopaySettings={autopaySettings ?? undefined}
        hasInterestReserve={hasInterestReserve}
        paymentDueStatus={paymentDueStatus}
        openPayoffStatus={openPayoffStatus ?? undefined}
        delinquentInterestSettings={delinquentInterestSettings}
        currentBillSettingsType={currentBillSettingsType ?? LoanBillSettingsType.Borrower}
        activeWorkoutPlan={activeWorkoutPlan ?? undefined}
        offeredWorkoutPlans={offeredWorkoutPlans}
      />
      <PaymentDetails companyId={company.id} loanId={loanId} payments={processingAndRecentPayments} refetch={refetch} />

      {displayPaymentForm ? (
        <PaymentForm
          loanId={loanId}
          loanStatus={loanStatus}
          company={company}
          totalAmountDue={totalAmountDue}
          totalUpcomingAmountDue={totalUpcomingAmountDue}
          totalAmountDueToReinstateLoan={totalAmountDueToReinstateLoan}
          paymentsDue={paymentsDue}
          chargesDue={chargesDue}
          nachaEnabled={manualNachaFileGeneration ?? false}
          achAccounts={loan.achAccounts}
          repayEnabled={repayEnabled ?? false}
          repayAccounts={loan.repayAccounts}
          autopaySettings={autopaySettings ?? undefined}
          onPaymentSuccess={handlePaymentSubmission}
          refetch={refetch}
          exactPaymentOnly={exactPaymentOnly}
          shortageAmount={shortageAmount}
          spreadMonths={spreadMonths}
          escrowShortageAnalysisId={escrowShortageAnalysisId ?? undefined}
          activeWorkoutPlan={activeWorkoutPlan ?? undefined}
          escrowBalance={escrowBalance}
          isPastDue={paymentDueSubStatus?.isPastDue}
          totalStatementAmountDue={totalStatementAmountDue}
          isForbearanceOrFlexModificationWorkoutPlan={isForbearanceOrFlexModificationWorkoutPlan}
        />
      ) : (
        <button
          className="payment-accepted__btn"
          data-ghost="portal-loan--make-a-payment-button"
          onClick={() => setDisplayPaymentForm(true)}
        >
          Make a Payment
        </button>
      )}
    </>
  );
};
